import { ChevronLeft, ChevronRight, Copy, ExternalLink, FileText, ImageIcon, LayoutGrid, List, Lock, Sparkles, Trash2, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useOutletContext, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useIdiomas } from '../api/idiomas'
import { EditorDeTraducciones } from '../motor/campos/Traducciones'

import { useInterruptores } from '../api/interruptores'
import { type LoteDelNucleo, lotesAbiertos } from '../api/lotes'
import {
  type CalidadDeImagen,
  familiaDe,
  type FiltrosDeMedios,
  type FormatoDeImagen,
  miniaturaDe,
  tamano,
  tarjetaDe,
  useBorrarMedio,
  useCambiarVisibilidad,
  useCosteDeImagen,
  useCrearImagen,
  useGuardarMedio,
  useMedios,
  usosDe,
  useUsoDeMedios,
  type Medio,
} from '../api/medios'
import { ErrorDeLaApi } from '../api/nucleo'
import type { Permisos } from '../api/capacidades'
import type { UsoDeUnMedio } from '../api/tipos'
import { Aviso, BarraDeBusqueda, Boton, Esqueleto, EstadoVacio, Modal, Pastilla, Selector, Tarjeta, Titulo, claseDeEntrada, clases } from '../componentes/ui'
import { SubidaMultiple } from '../medios/SubidaPorLotes'
import { SubidaDeUnaImagen } from '../medios/SubidaSencilla'
import { useSubidaPorLotes } from '../medios/useSubidaPorLotes'
import { sinCreditos } from './diseno/textos'
import type { Contexto } from './Modulo'

/**
 * La biblioteca de medios (CLAUDE.md §8 y plan del CMS dinámico §10–§13): imágenes, SVG y
 * documentos de esta web, en **lista** o en **cuadrícula** (se recuerda en este navegador), con
 * filtros por tipo, visibilidad, estado, subida y carpeta. **Subir** es por lotes: muchos ficheros
 * a la vez, de tres en tres, con su progreso, pausa y reanudación; lo que se quedó a medias con la
 * pestaña cerrada se ofrece al volver. Cada fichero lleva su texto alternativo, su punto focal (las
 * imágenes) y su visibilidad: público tiene dirección en la web; privado, solo un enlace que caduca.
 * Y **crear una imagen con IA**, que el núcleo guarda aquí como una subida más.
 */
const FORMATOS: Array<{ valor: FormatoDeImagen; etiqueta: string; forma: string }> = [
  { valor: 'horizontal', etiqueta: 'Horizontal', forma: 'h-6 w-9' },
  { valor: 'cuadrada', etiqueta: 'Cuadrada', forma: 'size-7' },
  { valor: 'vertical', etiqueta: 'Vertical', forma: 'h-9 w-6' },
]

const Opcion = ({ elegida, alElegir, children }: { alElegir: () => void; children: React.ReactNode; elegida: boolean }) => (
  <button
    aria-pressed={elegida}
    className={clases('flex flex-1 flex-col items-center gap-1.5 rounded-caja border-2 px-3 py-2.5 text-[13px] transition-colors', elegida ? 'border-violeta bg-violeta-suave/40 font-medium' : 'border-linea hover:border-linea-fuerte')}
    onClick={alElegir}
    type="button"
  >
    {children}
  </button>
)

const CrearConIA = ({ sitio, espacio, permisos, cerrar }: { cerrar: () => void; espacio: number; permisos: Permisos; sitio: number }) => {
  const [prompt, setPrompt] = useState('')
  const [formato, setFormato] = useState<FormatoDeImagen>('horizontal')
  const [calidad, setCalidad] = useState<CalidadDeImagen>('normal')
  const coste = useCosteDeImagen(sitio, formato, calidad)
  const crear = useCrearImagen(sitio, espacio)
  const hecha = crear.data
  // Sin saldo el botón no se ofrece como si fuera a funcionar: se dice por qué, y qué hacer según quién mira.
  const noLlega = Boolean(coste.data && coste.data.creditos > coste.data.saldo)
  const faltan = sinCreditos(permisos.tiene('creditos.comprar'))

  return (
    <Modal
      ancho="max-w-xl"
      cerrar={cerrar}
      pie={
        hecha ? (
          <>
            <Boton icono={<Sparkles />} onClick={() => crear.reset()}>
              Crear otra
            </Boton>
            <Boton onClick={cerrar} tipo="primario">
              Listo
            </Boton>
          </>
        ) : (
          <>
            <Boton onClick={cerrar}>Cancelar</Boton>
            <Boton cargando={crear.isPending} disabled={prompt.trim().length < 10 || coste.isError || noLlega} form="crear-con-ia" icono={<Sparkles />} tipo="primario" type="submit">
              {crear.isPending ? 'Creando… puede tardar un minuto' : 'Crear imagen'}
            </Boton>
          </>
        )
      }
      titulo="Crear una imagen con IA"
    >
      {hecha ? (
        <div className="flex flex-col gap-3">
          {hecha.medio.url && <img alt={hecha.medio.alt} className="w-full rounded-caja border border-linea" src={hecha.medio.url} />}
          <p className="text-sm">
            Ya está en tu biblioteca. Ha costado {hecha.creditos.consumidos} créditos; te quedan {hecha.creditos.saldo}.
          </p>
          <p className="text-[13px] text-tenue">Su texto alternativo es lo que pediste: ábrela en la biblioteca y ajústalo para que diga lo que se ve.</p>
        </div>
      ) : (
        <form
          className="flex flex-col gap-5"
          id="crear-con-ia"
          onSubmit={(e) => {
            e.preventDefault()
            crear.mutate({ prompt: prompt.trim(), formato, calidad })
          }}
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Describe la imagen</span>
            <textarea
              className={clases(claseDeEntrada(), 'min-h-28 resize-y')}
              maxLength={1000}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Una taza de café sobre una mesa de madera clara, luz de mañana, fondo desenfocado."
              required
              value={prompt}
            />
            <span className="text-xs text-tenue">Qué se ve, dónde y con qué luz. Sin marcas ni personas reales: el proveedor lo rechaza.</span>
          </label>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">Formato</legend>
            <div className="flex gap-2">
              {FORMATOS.map((f) => (
                <Opcion alElegir={() => setFormato(f.valor)} elegida={formato === f.valor} key={f.valor}>
                  <span aria-hidden className={clases('rounded-sm border-2 border-current opacity-60', f.forma)} />
                  {f.etiqueta}
                </Opcion>
              ))}
            </div>
          </fieldset>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">Calidad</legend>
            <div className="flex gap-2">
              <Opcion alElegir={() => setCalidad('normal')} elegida={calidad === 'normal'}>
                Normal
              </Opcion>
              <Opcion alElegir={() => setCalidad('alta')} elegida={calidad === 'alta'}>
                Alta (más detalle, cuesta más)
              </Opcion>
            </div>
          </fieldset>
          {crear.isError && <Aviso tono="error">{crear.error.message}</Aviso>}
          <p className="text-[13px] text-tenue">
            {coste.isError ? coste.error.message : coste.data ? `Cuesta unos ${coste.data.creditos} créditos. Te quedan ${coste.data.saldo}.` : 'Calculando lo que cuesta…'}
          </p>
          {noLlega && (
            <Aviso>
              {faltan.texto}
              {faltan.enlace && (
                <>
                  {' '}
                  <Link className="font-medium text-violeta underline-offset-2 hover:underline" to={`/e/${espacio}/creditos`}>
                    {faltan.enlace}
                  </Link>
                </>
              )}
            </Aviso>
          )}
        </form>
      )}
    </Modal>
  )
}

/** Dónde se edita cada uso, en este panel. Lo que no se edita desde aquí va sin enlace. */
const enlaceDeUso = (u: UsoDeUnMedio, espacio: number, sitio: number): null | string =>
  !u.modulo ? null : u.modulo === 'ajustes' ? `/e/${espacio}/s/${sitio}/ajustes` : `/e/${espacio}/s/${sitio}/${u.modulo}/${u.id}`

/**
 * Antes de borrar se dice **dónde se usa**: al borrarla, la base de datos la quita sola de
 * cada sitio donde estaba, también de la web publicada. Lo publicado va primero, marcado.
 */
const UsosAntesDeBorrar = ({ usos, espacio, sitio }: { espacio: number; sitio: number; usos: UsoDeUnMedio[] }) => {
  if (usos.length === 0) return <Aviso tono="info">Esta imagen no se usa en ninguna página, entrada, producto ni en los ajustes. Se puede borrar sin que cambie nada de tu web.</Aviso>
  const publicados = usos.filter((u) => u.publicado).length
  return (
    <Aviso tono={publicados > 0 ? 'error' : 'aviso'}>
      <p>
        {publicados > 0
          ? `Esta imagen se ve en tu web. Si la borras, desaparecerá de ${publicados === 1 ? 'este sitio' : 'estos sitios'}:`
          : 'Esta imagen no se ve en tu web, pero la usan estos borradores: si la borras, se quedarán sin ella.'}
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {usos.map((u) => {
          const a = enlaceDeUso(u, espacio, sitio)
          const nombre = u.coleccion === 'ajustes' || u.coleccion === 'socios' ? u.etiqueta : `${u.etiqueta} «${u.titulo}»`
          return (
            <li key={`${u.coleccion}:${u.id}`}>
              {a ? (
                <Link className="font-medium text-violeta underline-offset-2 hover:underline" to={a}>
                  {nombre}
                </Link>
              ) : (
                <span className="font-medium">{nombre}</span>
              )}
              <span className="text-tinta-2">
                {' '}
                — {u.donde.join(', ')}
                {u.publicado ? '' : ' (borrador)'}
              </span>
            </li>
          )
        })}
      </ul>
      {publicados > 0 && <p className="mt-2">Mejor cámbiala primero por otra en esos sitios. Si aun así quieres borrarla, confírmalo.</p>}
    </Aviso>
  )
}

/** El tipo de un fichero en una palabra («PDF», «Word»), por su tipo de verdad. */
const TIPOS: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/avif': 'AVIF',
  'image/gif': 'GIF',
  'image/svg+xml': 'SVG',
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/msword': 'Word antiguo',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-excel': 'Excel antiguo',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  'application/vnd.ms-powerpoint': 'PowerPoint antiguo',
  'text/csv': 'CSV',
  'text/plain': 'Texto',
}
const tipoDe = (m: Medio): string => TIPOS[m.tipoReal || m.mimeType || ''] ?? '—'

/** Las medidas de una imagen, o las páginas de un PDF. */
const medidasDe = (m: Medio): string => {
  const ancho = m.ancho ?? m.width
  const alto = m.alto ?? m.height
  if (ancho && alto) return `${ancho}×${alto}`
  if (m.paginas) return m.paginas === 1 ? '1 página' : `${m.paginas} páginas`
  return '—'
}

const ESTADO: Record<NonNullable<Medio['estado']>, { texto: string; clave: 'construyendo' | 'fallida' | 'publicado' }> = {
  pendiente: { texto: 'En espera', clave: 'construyendo' },
  subiendo: { texto: 'Subiendo', clave: 'construyendo' },
  analizando: { texto: 'Analizando', clave: 'construyendo' },
  procesando: { texto: 'Procesando', clave: 'construyendo' },
  listo: { texto: 'Listo', clave: 'publicado' },
  fallido: { texto: 'No se subió', clave: 'fallida' },
  bloqueado: { texto: 'Bloqueado', clave: 'fallida' },
}

const PastillaDeEstado = ({ m }: { m: Medio }) => {
  const e = ESTADO[m.estado ?? 'listo']
  return <Pastilla clave={e.clave}>{e.texto}</Pastilla>
}

const PastillaDeVisibilidad = ({ m }: { m: Medio }) =>
  m.visibilidad === 'privado' ? (
    <Pastilla clave="violeta" punto={false}>
      <Lock aria-hidden className="size-3" /> Privado
    </Pastilla>
  ) : (
    <Pastilla clave="neutra">Público</Pastilla>
  )

/** Lo que se ve de un medio en pequeño: la imagen, o un icono con su tipo si es un documento (o aún no tiene dirección). */
const Vistazo = ({ m, clase }: { clase: string; m: Medio }) => {
  const familia = familiaDe(m)
  // Un PDF listo enseña su primera página, que dibuja el núcleo (`/api/medios/miniatura`); si no se puede, el icono.
  const [sinMiniatura, setSinMiniatura] = useState(false)
  const pdf = m.mimeType === 'application/pdf' && m.estado === 'listo' && !sinMiniatura
  const src = familia === 'imagen' || familia === 'svg' ? miniaturaDe(m) : pdf ? `/api/medios/miniatura?id=${m.id}` : null
  if (src) return <img alt={m.alt} className={clases(clase, pdf ? 'bg-white object-contain' : 'object-cover')} loading="lazy" onError={pdf ? () => setSinMiniatura(true) : undefined} src={src} />
  return (
    <span className={clases(clase, 'flex flex-col items-center justify-center gap-1 bg-papel-2 text-tenue')}>
      {familia === 'imagen' ? <ImageIcon aria-hidden className="size-6" /> : <FileText aria-hidden className="size-6" />}
      <span className="text-[11px] font-medium">{tipoDe(m)}</span>
    </span>
  )
}

/** Copiar la dirección: la del CDN si es público; si es privado, un enlace que caduca (y se dice). */
const CopiarDireccion = ({ m }: { m: Medio }) => {
  const [hecho, setHecho] = useState(false)
  if (!m.url) return <span className="text-xs text-tenue">—</span>
  return (
    <button
      className="inline-flex items-center gap-1 text-xs text-violeta hover:underline"
      onClick={(e) => {
        e.stopPropagation()
        void navigator.clipboard
          ?.writeText(m.url!)
          .then(() => {
            setHecho(true)
            setTimeout(() => setHecho(false), 2000)
          })
          .catch(() => undefined)
      }}
      title={m.visibilidad === 'privado' ? 'Enlace temporal: caduca a los diez minutos' : m.url}
      type="button"
    >
      <Copy aria-hidden className="size-3.5" />
      {hecho ? 'Copiada' : m.visibilidad === 'privado' ? 'Copiar enlace temporal' : 'Copiar dirección'}
    </button>
  )
}

const Ficha = ({ medio, sitio, espacio, permisos, cerrar }: { cerrar: () => void; espacio: number; medio: Medio; permisos: Permisos; sitio: number }) => {
  const guardar = useGuardarMedio(sitio)
  const borrar = useBorrarMedio(sitio, espacio)
  const visibilidad = useCambiarVisibilidad(sitio)
  // Sin la biblioteca v2 (lo dice el núcleo) no hay privados: no se ofrece cambiar la visibilidad.
  const { mediosV2 } = useInterruptores()
  const [alt, setAlt] = useState(medio.alt)
  const [titulo, setTitulo] = useState(medio.titulo ?? '')
  const [traducciones, setTraducciones] = useState(medio.traducciones ?? null)
  const { data: idiomasDeLaWeb } = useIdiomas(sitio)
  const [carpeta, setCarpeta] = useState(medio.carpeta ?? '')
  const [foco, setFoco] = useState({ x: medio.focalX ?? 50, y: medio.focalY ?? 50 })
  const [mensaje, setMensaje] = useState<null | string>(null)
  // Al pulsar «Borrar» primero se mira dónde se usa; el borrado va después, confirmado.
  const [usos, setUsos] = useState<null | UsoDeUnMedio[]>(null)
  const [mirando, setMirando] = useState(false)
  const [confirmarPrivado, setConfirmarPrivado] = useState<null | UsoDeUnMedio[]>(null)
  const familia = familiaDe(medio)
  const imagen = familia === 'imagen'
  const grande = familia === 'imagen' || familia === 'svg' ? tarjetaDe(medio) : null
  const listo = !medio.estado || medio.estado === 'listo'

  const pedirBorrar = async () => {
    setMensaje(null)
    setMirando(true)
    try {
      setUsos((await usosDe(medio.id)).usos)
    } catch (fallo) {
      setMensaje(fallo instanceof ErrorDeLaApi ? fallo.message : 'No hemos podido comprobar dónde se usa. Vuelve a intentarlo.')
    } finally {
      setMirando(false)
    }
  }
  /** Antes de hacerlo privado se enseña dónde se ve: ahí dejará de verse. */
  const pedirPrivado = async () => {
    setMensaje(null)
    try {
      setConfirmarPrivado((await usosDe(medio.id)).usos.filter((u) => u.publicado))
    } catch (fallo) {
      setMensaje(fallo instanceof Error ? fallo.message : 'No hemos podido comprobar dónde se usa.')
    }
  }
  const cambiarA = (v: 'privado' | 'publico') =>
    visibilidad.mutate(
      { id: medio.id, visibilidad: v },
      {
        onSuccess: () => {
          setConfirmarPrivado(null)
          setMensaje(v === 'privado' ? 'Se está pasando a privado: en unos segundos solo se podrá descargar con un enlace que caduca.' : 'Se está pasando a público: en unos segundos tendrá su dirección en tu web.')
        },
        onError: (err) => setMensaje(err.message),
      },
    )
  const seVe = (usos ?? []).some((u) => u.publicado)

  return (
    <Modal
      ancho="max-w-3xl"
      cerrar={cerrar}
      pie={
        usos ? (
          <>
            <Boton disabled={borrar.isPending} onClick={() => setUsos(null)}>
              No borrarlo
            </Boton>
            <Boton
              cargando={borrar.isPending}
              icono={<Trash2 />}
              onClick={() => borrar.mutate(medio.id, { onSuccess: cerrar, onError: (err) => setMensaje(err.message) })}
              tipo="peligro"
            >
              {seVe ? 'Borrar igualmente' : 'Borrar'}
            </Boton>
          </>
        ) : (
          <>
            <Boton cargando={mirando} icono={<Trash2 />} onClick={() => void pedirBorrar()} tipo="peligro">
              Borrar
            </Boton>
            <Boton cargando={guardar.isPending} form="ficha-de-medio" tipo="primario" type="submit">
              Guardar
            </Boton>
          </>
        )
      }
      titulo={medio.alt}
    >
      {usos && (
        <div className="mb-5">
          <UsosAntesDeBorrar espacio={espacio} sitio={sitio} usos={usos} />
        </div>
      )}
      {!listo && (
        <div className="mb-5">
          <Aviso tono={medio.estado === 'fallido' || medio.estado === 'bloqueado' ? 'error' : 'info'}>
            {medio.estado === 'bloqueado'
              ? medio.error || 'El antivirus encontró algo en este fichero y se ha borrado. Si crees que es un error, escríbenos.'
              : medio.estado === 'fallido'
                ? medio.error || 'Este fichero no se ha podido subir. Vuelve a subirlo.'
                : 'Se está analizando y preparando: en cuanto termine se podrá usar.'}
          </Aviso>
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-[1fr_17rem]">
        <div>
          {imagen && grande ? (
            <>
              <button
                aria-label="Fijar el punto focal"
                className="relative block w-full cursor-crosshair overflow-hidden rounded-caja border border-linea bg-papel-2"
                onClick={(e) => {
                  const caja = e.currentTarget.getBoundingClientRect()
                  setFoco({ x: Math.round(((e.clientX - caja.left) / caja.width) * 100), y: Math.round(((e.clientY - caja.top) / caja.height) * 100) })
                }}
                type="button"
              >
                <img alt={medio.alt} className="block max-h-[56vh] w-full object-contain" src={grande} />
                <span
                  aria-hidden
                  className="absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-violeta/80 shadow-alta"
                  style={{ left: `${foco.x}%`, top: `${foco.y}%` }}
                />
              </button>
              <p className="mt-2 text-xs text-tenue">Pulsa sobre la imagen para fijar el punto que nunca debe recortarse.</p>
            </>
          ) : grande ? (
            <img alt={medio.alt} className="block max-h-[56vh] w-full rounded-caja border border-linea bg-papel-2 object-contain p-4" src={grande} />
          ) : (
            <Vistazo clase="h-56 w-full rounded-caja border border-linea" m={medio} />
          )}
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-tenue">
            <dt>Tipo</dt>
            <dd className="text-tinta-2">{tipoDe(medio)}</dd>
            <dt>Tamaño</dt>
            <dd className="text-tinta-2 tabular-nums">
              {tamano(medio.filesize)} · {medidasDe(medio)}
            </dd>
            <dt>Fichero</dt>
            <dd className="truncate text-tinta-2">{medio.filename || medio.nombreOriginal || '—'}</dd>
            <dt>Se usa</dt>
            <dd className="text-tinta-2">{medio.usos ? (medio.usos === 1 ? 'en 1 sitio' : `en ${medio.usos} sitios`) : 'en ningún sitio todavía'}</dd>
          </dl>
        </div>
        <form
          className="flex flex-col gap-4"
          id="ficha-de-medio"
          onSubmit={(e) => {
            e.preventDefault()
            guardar.mutate(
              { id: medio.id, datos: { alt: alt.trim(), titulo: titulo.trim() || null, traducciones, carpeta: carpeta.trim() || null, ...(imagen ? { focalX: foco.x, focalY: foco.y } : {}) } },
              { onSuccess: () => setMensaje('Guardado.'), onError: (err) => setMensaje(err.message) },
            )
          }}
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">{imagen ? 'Texto alternativo' : 'Descripción'}</span>
            <textarea className={clases(claseDeEntrada(), 'h-auto min-h-24 resize-y py-2')} onChange={(e) => setAlt(e.target.value)} required value={alt} />
            <span className="text-xs text-tenue">{imagen ? 'Qué se ve en la imagen. Lo leen quienes no la ven y los buscadores.' : 'Qué es este fichero, en una frase.'}</span>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Título</span>
            <input className={claseDeEntrada()} onChange={(e) => setTitulo(e.target.value)} value={titulo} />
          </label>
          {idiomasDeLaWeb && idiomasDeLaWeb.encendidos.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">En los otros idiomas de tu web</span>
              <EditorDeTraducciones cambiar={setTraducciones} coleccion="medios" datos={{ alt, titulo }} sitio={sitio} valor={traducciones} />
            </div>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Carpeta</span>
            <input className={claseDeEntrada()} maxLength={120} onChange={(e) => setCarpeta(e.target.value)} placeholder="Sin carpeta" value={carpeta} />
          </label>
          <div className="flex flex-col gap-2 rounded-caja border border-linea p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] font-medium">Visibilidad</span>
              <PastillaDeVisibilidad m={medio} />
            </div>
            <p className="text-xs text-tenue">
              {medio.visibilidad === 'privado' ? 'Solo se descarga con un enlace que caduca a los diez minutos. No tiene dirección en tu web.' : 'Tiene dirección en tu web: cualquiera con el enlace lo ve.'}
            </p>
            {permisos.tiene('medios.privados') && mediosV2 && listo && !confirmarPrivado && (
              <Boton cargando={visibilidad.isPending} onClick={() => (medio.visibilidad === 'privado' ? cambiarA('publico') : void pedirPrivado())} pequeno>
                {medio.visibilidad === 'privado' ? 'Hacerlo público' : 'Hacerlo privado'}
              </Boton>
            )}
            {confirmarPrivado && (
              <>
                <Aviso tono={confirmarPrivado.length > 0 ? 'aviso' : 'info'}>
                  {confirmarPrivado.length > 0
                    ? `Se ve en ${confirmarPrivado.map((u) => (u.coleccion === 'ajustes' ? u.etiqueta : `${u.etiqueta.toLowerCase()} «${u.titulo}»`)).join(', ')}. Al hacerlo privado dejará de verse ahí.`
                    : 'No se ve en ninguna página publicada.'}
                </Aviso>
                <div className="flex gap-2">
                  <Boton onClick={() => setConfirmarPrivado(null)} pequeno>
                    No cambiar
                  </Boton>
                  <Boton cargando={visibilidad.isPending} icono={<Lock />} onClick={() => cambiarA('privado')} pequeno tipo="primario">
                    Hacerlo privado
                  </Boton>
                </div>
              </>
            )}
          </div>
          {mensaje && <Aviso tono={guardar.isError || borrar.isError || visibilidad.isError ? 'error' : 'ok'}>{mensaje}</Aviso>}
          {medio.url && (
            <a className="inline-flex items-center gap-1 text-xs text-violeta hover:underline" href={medio.url} rel="noreferrer" target="_blank">
              <ExternalLink className="size-3.5" />
              {medio.visibilidad === 'privado' ? 'Descargar (enlace temporal)' : imagen ? 'Abrir el original' : 'Abrir'}
            </a>
          )}
        </form>
      </div>
    </Modal>
  )
}

type Vista = 'cuadricula' | 'lista'
const CLAVE_DE_VISTA = 'mc.medios.vista'
const leerVista = (): Vista => {
  try {
    return localStorage.getItem(CLAVE_DE_VISTA) === 'lista' ? 'lista' : 'cuadricula'
  } catch {
    return 'cuadricula'
  }
}
const guardarVista = (v: Vista) => {
  try {
    localStorage.setItem(CLAVE_DE_VISTA, v)
  } catch {
    // Sin almacenamiento (navegación privada), la vista dura lo que dure la pantalla.
  }
}

const FILTROS = ['tipo', 'visibilidad', 'estado', 'lote', 'carpeta'] as const
const fecha = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

export const Medios = () => {
  const { espacio, permisos, sitio } = useOutletContext<Contexto>()
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const pagina = Math.max(1, Number(params.get('pagina')) || 1)
  const filtros: FiltrosDeMedios = {
    tipo: (params.get('tipo') ?? '') as FiltrosDeMedios['tipo'],
    visibilidad: (params.get('visibilidad') ?? '') as FiltrosDeMedios['visibilidad'],
    estado: (params.get('estado') ?? '') as FiltrosDeMedios['estado'],
    lote: params.get('lote') ?? '',
    carpeta: params.get('carpeta') ?? '',
  }
  const [vista, setVista] = useState<Vista>(leerVista)
  const [subiendo, setSubiendo] = useState(false)
  const [retomando, setRetomando] = useState<LoteDelNucleo | null>(null)
  const [creando, setCreando] = useState(false)
  const [abierto, setAbierto] = useState<Medio | null>(null)
  const { data, isPending } = useMedios(sitio?.id ?? 0, q, pagina, 40, filtros)
  const { data: uso } = useUsoDeMedios(espacio)
  const subida = useSubidaPorLotes(sitio?.id ?? 0, espacio)
  // Con la biblioteca v2 apagada en la instalación (lo dice el núcleo): una imagen cada vez, sin lotes ni privados.
  const { mediosV2 } = useInterruptores()
  const abiertos = useQuery({ queryKey: ['lotes-abiertos', sitio?.id], enabled: Boolean(sitio) && mediosV2, queryFn: () => lotesAbiertos(sitio!.id) })
  const aMedias = (abiertos.data?.lotes ?? []).filter((l) => (l.sinSubir ?? 0) > 0 && l.id !== subida.lote?.id)
  const sinTerminar = aMedias.reduce((s, l) => s + (l.sinSubir ?? 0), 0)

  useEffect(() => guardarVista(vista), [vista])

  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  const porcentaje = uso ? Math.min(100, Math.round((uso.usadoBytes / Math.max(1, uso.cuotaBytes)) * 100)) : 0
  const totalPaginas = data?.totalPages ?? 1
  const hayFiltros = FILTROS.some((f) => params.get(f))
  const filtrar = (clave: (typeof FILTROS)[number], valor: string) => {
    const nuevos = new URLSearchParams(params)
    if (valor) nuevos.set(clave, valor)
    else nuevos.delete(clave)
    nuevos.delete('pagina')
    setParams(nuevos, { replace: true })
  }
  const aPagina = (n: number) => {
    const nuevos = new URLSearchParams(params)
    nuevos.set('pagina', String(n))
    setParams(nuevos)
  }
  const enMarcha = subida.fase === 'subiendo' || subida.fase === 'pausado' || subida.fase === 'creando'

  return (
    <>
      <Titulo
        accion={
          <div className="flex flex-wrap gap-2">
            <Boton icono={<Sparkles />} onClick={() => setCreando(true)}>
              Crear con IA
            </Boton>
            <Boton
              icono={<Upload />}
              onClick={() => {
                setRetomando(null)
                setSubiendo(true)
              }}
              tipo="primario"
            >
              {mediosV2 ? 'Subir ficheros' : 'Subir imagen'}
            </Boton>
          </div>
        }
        descripcion={mediosV2 ? 'Las imágenes y los documentos de esta web. Lo público tiene dirección en tu web; lo privado solo se descarga con un enlace que caduca.' : 'Las imágenes de esta web. Cada una lleva su texto alternativo y su punto focal.'}
      >
        Medios
      </Titulo>

      {sinTerminar > 0 && !enMarcha && (
        <div className="mb-4">
          <Aviso tono="info">
            <span className="flex flex-wrap items-center gap-3">
              <span>{sinTerminar === 1 ? 'Tienes 1 fichero sin terminar de subir.' : `Tienes ${sinTerminar} ficheros sin terminar de subir.`}</span>
              <Boton
                onClick={() => {
                  setRetomando(aMedias[0] ?? null)
                  subida.limpiar()
                  setSubiendo(true)
                }}
                pequeno
                tipo="primario"
              >
                Reanudar
              </Boton>
            </span>
          </Aviso>
        </div>
      )}
      {enMarcha && !subiendo && (
        <div className="mb-4">
          <Aviso tono="info">
            <span className="flex flex-wrap items-center gap-3">
              <span>
                {subida.fase === 'pausado' ? 'Subida en pausa' : 'Subiendo'}: {subida.cuentas.listos} de {subida.cuentas.total} listos.
              </span>
              <Boton onClick={() => setSubiendo(true)} pequeno>
                Ver la subida
              </Boton>
            </span>
          </Aviso>
        </div>
      )}

      <Tarjeta sinRelleno>
        <div className="flex flex-wrap items-center gap-3 border-b border-linea px-4 py-3">
          <BarraDeBusqueda
            alBuscar={(t) => {
              const nuevos = new URLSearchParams(params)
              if (t) nuevos.set('q', t)
              else nuevos.delete('q')
              nuevos.delete('pagina')
              setParams(nuevos, { replace: true })
            }}
            className="w-full sm:w-72"
            placeholder="Buscar por descripción o nombre…"
            valor={q}
          />
          <div className="min-w-0 flex-1 basis-40 sm:w-44 sm:flex-none">
            <Selector aria-label="Tipo" onChange={(e) => filtrar('tipo', e.target.value)} pequeno value={filtros.tipo}>
              <option value="">Todos los tipos</option>
              <option value="imagenes">Imágenes</option>
              <option value="svg">SVG</option>
              <option value="documentos">Documentos</option>
            </Selector>
          </div>
          <div className="min-w-0 flex-1 basis-40 sm:w-44 sm:flex-none">
            <Selector aria-label="Visibilidad" onChange={(e) => filtrar('visibilidad', e.target.value)} pequeno value={filtros.visibilidad}>
              <option value="">Público y privado</option>
              <option value="publico">Público</option>
              <option value="privado">Privado</option>
            </Selector>
          </div>
          <div className="min-w-0 flex-1 basis-40 sm:w-44 sm:flex-none">
            <Selector aria-label="Estado" onChange={(e) => filtrar('estado', e.target.value)} pequeno value={filtros.estado}>
              <option value="">Cualquier estado</option>
              <option value="listo">Listos</option>
              <option value="en-curso">Subiendo o analizando</option>
              <option value="fallido">No se subieron</option>
              <option value="bloqueado">Bloqueados</option>
            </Selector>
          </div>
          {(filtros.carpeta || filtros.lote) && (
            <span className="flex flex-wrap gap-2 text-xs">
              {filtros.carpeta && (
                <Boton onClick={() => filtrar('carpeta', '')} pequeno tipo="discreto">
                  Carpeta «{filtros.carpeta}» ×
                </Boton>
              )}
              {filtros.lote && (
                <Boton onClick={() => filtrar('lote', '')} pequeno tipo="discreto">
                  Subida {filtros.lote} ×
                </Boton>
              )}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1" role="group" aria-label="Cómo verlos">
            <button aria-pressed={vista === 'cuadricula'} className={clases('rounded-md p-1.5', vista === 'cuadricula' ? 'bg-violeta-suave text-violeta' : 'text-tenue hover:text-tinta')} onClick={() => setVista('cuadricula')} title="Cuadrícula" type="button">
              <LayoutGrid className="size-4" />
            </button>
            <button aria-pressed={vista === 'lista'} className={clases('rounded-md p-1.5', vista === 'lista' ? 'bg-violeta-suave text-violeta' : 'text-tenue hover:text-tinta')} onClick={() => setVista('lista')} title="Lista" type="button">
              <List className="size-4" />
            </button>
          </div>
          {uso && (
            <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-xs text-tenue">
              <span className="whitespace-nowrap tabular-nums">
                {tamano(uso.usadoBytes)} de {tamano(uso.cuotaBytes)}
              </span>
              <span className="h-1.5 w-28 shrink-0 overflow-hidden rounded-full bg-papel-3">
                <span className={clases('block h-full rounded-full', porcentaje > 90 ? 'bg-error' : porcentaje > 75 ? 'bg-aviso' : 'bg-violeta')} style={{ width: `${porcentaje}%` }} />
              </span>
              <span className="whitespace-nowrap">hasta {tamano(uso.maximoPorFicheroBytes)} por fichero</span>
            </div>
          )}
        </div>

        <div className={vista === 'lista' ? '' : 'p-4'}>
          {isPending ? (
            <div className="p-4">
              <Esqueleto />
            </div>
          ) : !data?.docs.length ? (
            <EstadoVacio
              accion={
                !q && !hayFiltros ? (
                  <Boton icono={<Upload />} onClick={() => setSubiendo(true)} tipo="primario">
                    Subir los primeros ficheros
                  </Boton>
                ) : undefined
              }
              descripcion={q || hayFiltros ? 'Prueba con otras palabras o quita algún filtro.' : 'Lo que subas aquí lo podrás elegir en cualquier página, entrada o producto.'}
              icono={<ImageIcon />}
              titulo={q || hayFiltros ? 'Nada coincide con lo que buscas' : 'Todavía no tienes ficheros'}
            />
          ) : vista === 'cuadricula' ? (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {data.docs.map((m) => (
                <li key={m.id}>
                  <button
                    className="group block w-full overflow-hidden rounded-caja border border-linea bg-papel text-left transition-all hover:border-violeta-borde hover:shadow-media"
                    onClick={() => setAbierto(m)}
                    type="button"
                  >
                    <span className="relative block aspect-square overflow-hidden bg-papel-2">
                      <Vistazo clase="size-full transition-transform duration-300 group-hover:scale-[1.03]" m={m} />
                      <span className="absolute top-2 left-2 flex gap-1">
                        {m.estado && m.estado !== 'listo' && <PastillaDeEstado m={m} />}
                        {m.visibilidad === 'privado' && <PastillaDeVisibilidad m={m} />}
                      </span>
                    </span>
                    <span className="block px-3 py-2">
                      <span className="block truncate text-[13px] font-medium">{m.alt}</span>
                      <span className="block truncate text-xs text-tenue tabular-nums">
                        {medidasDe(m)} · {tamano(m.filesize)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13px]">
                <thead className="border-b border-linea text-xs text-tenue">
                  <tr>
                    <th className="px-4 py-2 font-medium">Nombre</th>
                    <th className="px-2 py-2 font-medium">Tipo</th>
                    <th className="px-2 py-2 font-medium">Tamaño</th>
                    <th className="px-2 py-2 font-medium">Medidas</th>
                    <th className="px-2 py-2 font-medium">Fecha</th>
                    <th className="px-2 py-2 font-medium">Usado en</th>
                    <th className="px-2 py-2 font-medium">Visibilidad</th>
                    <th className="px-2 py-2 font-medium">Estado</th>
                    <th className="px-4 py-2 font-medium">Dirección</th>
                  </tr>
                </thead>
                <tbody>
                  {data.docs.map((m) => (
                    <tr className="cursor-pointer border-b border-linea last:border-b-0 hover:bg-papel-2/60" key={m.id} onClick={() => setAbierto(m)}>
                      <td className="px-4 py-2">
                        <span className="flex items-center gap-2.5">
                          <Vistazo clase="size-9 shrink-0 overflow-hidden rounded-md border border-linea" m={m} />
                          <span className="min-w-0">
                            <span className="block max-w-64 truncate font-medium">{m.alt}</span>
                            <span className="block max-w-64 truncate text-xs text-tenue">{m.filename || m.nombreOriginal}</span>
                          </span>
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-tinta-2">{tipoDe(m)}</td>
                      <td className="px-2 py-2 whitespace-nowrap tabular-nums text-tinta-2">{tamano(m.filesize)}</td>
                      <td className="px-2 py-2 whitespace-nowrap tabular-nums text-tinta-2">{medidasDe(m)}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-tinta-2">{fecha(m.createdAt ?? m.updatedAt)}</td>
                      <td className="px-2 py-2 whitespace-nowrap tabular-nums text-tinta-2">{m.usos ? m.usos : '—'}</td>
                      <td className="px-2 py-2">
                        <PastillaDeVisibilidad m={m} />
                      </td>
                      <td className="px-2 py-2">
                        <PastillaDeEstado m={m} />
                      </td>
                      <td className="px-4 py-2">
                        <CopiarDireccion m={m} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data && data.docs.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-linea px-4 py-2.5 text-[13px] text-tenue">
            <span>
              {data.totalDocs} {data.totalDocs === 1 ? 'fichero' : 'ficheros'}
              {totalPaginas > 1 ? ` · página ${pagina} de ${totalPaginas}` : ''}
            </span>
            {totalPaginas > 1 && (
              <div className="flex gap-1">
                <Boton disabled={pagina <= 1} icono={<ChevronLeft />} onClick={() => aPagina(pagina - 1)} pequeno>
                  Anterior
                </Boton>
                <Boton disabled={pagina >= totalPaginas} onClick={() => aPagina(pagina + 1)} pequeno>
                  Siguiente
                  <ChevronRight className="size-4" />
                </Boton>
              </div>
            )}
          </div>
        )}
      </Tarjeta>

      {subiendo &&
        (mediosV2 ? (
          <Modal ancho="max-w-2xl" cerrar={() => setSubiendo(false)} titulo={retomando ? 'Terminar una subida a medias' : 'Subir ficheros'}>
            <SubidaMultiple permitePrivado={permisos.tiene('medios.privados')} retomando={retomando} subida={subida} />
          </Modal>
        ) : (
          <Modal ancho="max-w-lg" cerrar={() => setSubiendo(false)} titulo="Subir una imagen">
            <SubidaDeUnaImagen alSubir={() => setSubiendo(false)} espacio={espacio} sitio={sitio.id} texto="Subir" />
          </Modal>
        ))}
      {abierto && <Ficha cerrar={() => setAbierto(null)} espacio={espacio} medio={abierto} permisos={permisos} sitio={sitio.id} />}
      {creando && <CrearConIA cerrar={() => setCreando(false)} espacio={espacio} permisos={permisos} sitio={sitio.id} />}
    </>
  )
}
